import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  ExternalCourseLevel,
  StudentAdLoginData,
  StudentFormSubmission,
  SubjectSearchItem,
} from '@/models/current-student-advanced-standing-form/api'
import {
  filterExternalCoursesWithSubjects,
  filterPreviousLearningExternalCourses,
  hasWorkExperienceMissingRequestedSubject,
} from '@/models/current-student-advanced-standing-form/previousLearning'
import { hasLegacyUnspecifiedCredit } from '@/models/current-student-advanced-standing-form/subjectRequests'

export const useCurrentStudentApplicationStore = defineStore('currentStudentAdvancedStandingFormApplication', () => {
  const submission = ref<StudentFormSubmission | null>(null)
  const secureData = ref('')
  const courseLevelList = ref<ExternalCourseLevel[]>([])
  const electiveList = ref<SubjectSearchItem[]>([])

  const externalCourses = computed(() => {
    return submission.value?.external_courses ?? []
  })

  const previousLearningExternalCourses = computed(() => {
    return filterPreviousLearningExternalCourses(externalCourses.value, submission.value?.student_course_id)
  })

  const externalCoursesWithSubjects = computed(() => {
    return filterExternalCoursesWithSubjects(externalCourses.value)
  })

  const workExperiences = computed(() => {
    return submission.value?.work_experience ?? []
  })

  const workExperienceMissingRequestedSubject = computed(() => {
    return hasWorkExperienceMissingRequestedSubject(workExperiences.value)
  })

  const legacyUnspecifiedCreditDetected = computed(() => {
    return hasLegacyUnspecifiedCredit(externalCourses.value)
  })

  function reset(): void {
    submission.value = null
    secureData.value = ''
    courseLevelList.value = []
    electiveList.value = []
  }

  function setSubmission(nextSubmission: StudentFormSubmission): void {
    submission.value = nextSubmission
  }

  function applyLoginData(data: StudentAdLoginData): void {
    secureData.value = data.secure_data
    courseLevelList.value = data.course_level_list
    electiveList.value = data.elective_list
    setSubmission(data.submission)
  }

  return {
    submission,
    secureData,
    courseLevelList,
    electiveList,
    externalCourses,
    previousLearningExternalCourses,
    externalCoursesWithSubjects,
    workExperiences,
    workExperienceMissingRequestedSubject,
    legacyUnspecifiedCreditDetected,
    reset,
    setSubmission,
    applyLoginData,
  }
})
